import { useState, useEffect, useMemo } from "react";
import { 
  Plus, Pencil, Trash2, FileDown, User, Activity, Scale, 
  TrendingUp, Search, Calendar, Heart, Droplets, Bone, Flame, 
  Target, GitCompare, Trophy, CheckCircle2, Circle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Area, AreaChart, ReferenceLine
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const MEASUREMENTS = {
  gewicht: { label: "Gewicht", unit: "kg", color: "#3b82f6", icon: Scale, lowerIsBetter: true },
  koerperfett: { label: "Körperfett", unit: "%", color: "#f59e0b", icon: Flame, lowerIsBetter: true },
  muskelmasse: { label: "Muskelmasse", unit: "kg", color: "#10b981", icon: Activity, lowerIsBetter: false },
  bmi: { label: "BMI", unit: "", color: "#8b5cf6", icon: Target, lowerIsBetter: true },
  viszerales_fett: { label: "Viszerales Fett", unit: "", color: "#ef4444", icon: Heart, lowerIsBetter: true },
  fettmasse: { label: "Fettmasse", unit: "kg", color: "#f97316", icon: Flame, lowerIsBetter: true },
  fettfreies_koerpergewicht: { label: "Fettfreies Gewicht", unit: "kg", color: "#06b6d4", icon: Activity, lowerIsBetter: false },
  knochenmasse: { label: "Knochenmasse", unit: "kg", color: "#64748b", icon: Bone, lowerIsBetter: false },
  wassergehalt: { label: "Wassergehalt", unit: "%", color: "#0ea5e9", icon: Droplets, lowerIsBetter: false },
  teilumfang: { label: "Teilumfang", unit: "cm", color: "#a855f7", icon: Target, lowerIsBetter: true },
  biologisches_alter: { label: "Biologisches Alter", unit: "Jahre", color: "#ec4899", icon: Calendar, lowerIsBetter: true }
};

export default function BioimpedancePage({ onLogout }) {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modals
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isDiagnosisModalOpen, setIsDiagnosisModalOpen] = useState(false);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingDiagnosis, setEditingDiagnosis] = useState(null);
  
  // Compare state
  const [compareDiagnosis1, setCompareDiagnosis1] = useState(null);
  const [compareDiagnosis2, setCompareDiagnosis2] = useState(null);
  
  // Form data
  const [clientForm, setClientForm] = useState({ vorname: "", nachname: "", groesse: "", geburtsdatum: "" });
  const [diagnosisForm, setDiagnosisForm] = useState({
    datum: new Date().toISOString().split('T')[0],
    typ: "",
    naechste_bewertung: "",
    gewicht: "", koerperfett: "", muskelmasse: "", bmi: "",
    viszerales_fett: "", fettmasse: "", fettfreies_koerpergewicht: "",
    knochenmasse: "", wassergehalt: "", teilumfang: "", biologisches_alter: ""
  });
  const [goalForm, setGoalForm] = useState({
    metric: "gewicht",
    target_value: "",
    target_date: ""
  });

  useEffect(() => { loadClients(); }, []);
  
  useEffect(() => {
    if (selectedClient) {
      loadClientDetails(selectedClient.id);
      loadGoals(selectedClient.id);
    }
  }, [selectedClient?.id]);

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/bio/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(response.data.clients);
    } catch (error) {
      toast.error("Fehler beim Laden der Kunden");
    } finally {
      setLoading(false);
    }
  };

  const loadClientDetails = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/bio/clients/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDiagnoses(response.data.diagnoses);
    } catch (error) {
      toast.error("Fehler beim Laden der Diagnosen");
    }
  };

  const loadGoals = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/bio/goals?client_id=${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      setGoals(response.data.goals);
    } catch (error) {
      console.error("Error loading goals:", error);
    }
  };

  const filteredClients = useMemo(() => {
    if (!searchQuery) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(c => c.vorname.toLowerCase().includes(q) || c.nachname.toLowerCase().includes(q));
  }, [clients, searchQuery]);

  const openClientModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setClientForm({ vorname: client.vorname, nachname: client.nachname, groesse: client.groesse.toString(), geburtsdatum: client.geburtsdatum });
    } else {
      setEditingClient(null);
      setClientForm({ vorname: "", nachname: "", groesse: "", geburtsdatum: "" });
    }
    setIsClientModalOpen(true);
  };

  const openDiagnosisModal = (diagnosis = null) => {
    if (diagnosis) {
      setEditingDiagnosis(diagnosis);
      setDiagnosisForm({
        datum: diagnosis.datum,
        typ: diagnosis.typ || "",
        naechste_bewertung: diagnosis.naechste_bewertung || "",
        gewicht: diagnosis.gewicht?.toString() || "",
        koerperfett: diagnosis.koerperfett?.toString() || "",
        muskelmasse: diagnosis.muskelmasse?.toString() || "",
        bmi: diagnosis.bmi?.toString() || "",
        viszerales_fett: diagnosis.viszerales_fett?.toString() || "",
        fettmasse: diagnosis.fettmasse?.toString() || "",
        fettfreies_koerpergewicht: diagnosis.fettfreies_koerpergewicht?.toString() || "",
        knochenmasse: diagnosis.knochenmasse?.toString() || "",
        wassergehalt: diagnosis.wassergehalt?.toString() || "",
        teilumfang: diagnosis.teilumfang?.toString() || "",
        biologisches_alter: diagnosis.biologisches_alter?.toString() || ""
      });
    } else {
      setEditingDiagnosis(null);
      setDiagnosisForm({
        datum: new Date().toISOString().split('T')[0],
        typ: "",
        naechste_bewertung: "",
        gewicht: "", koerperfett: "", muskelmasse: "", bmi: "",
        viszerales_fett: "", fettmasse: "", fettfreies_koerpergewicht: "",
        knochenmasse: "", wassergehalt: "", teilumfang: "", biologisches_alter: ""
      });
    }
    setIsDiagnosisModalOpen(true);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = { ...clientForm, groesse: parseFloat(clientForm.groesse) };
      
      if (editingClient) {
        await axios.put(`${API}/api/bio/clients/${editingClient.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Kunde aktualisiert");
      } else {
        await axios.post(`${API}/api/bio/clients`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Kunde erstellt");
      }
      setIsClientModalOpen(false);
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Möchten Sie ${client.vorname} ${client.nachname} und alle Diagnosen löschen?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/bio/clients/${client.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Kunde gelöscht");
      setSelectedClient(null);
      setDiagnoses([]);
      setGoals([]);
      loadClients();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleSaveDiagnosis = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        client_id: selectedClient.id,
        datum: diagnosisForm.datum,
        typ: diagnosisForm.typ || null,
        naechste_bewertung: diagnosisForm.naechste_bewertung || null,
        gewicht: diagnosisForm.gewicht ? parseFloat(diagnosisForm.gewicht) : null,
        koerperfett: diagnosisForm.koerperfett ? parseFloat(diagnosisForm.koerperfett) : null,
        muskelmasse: diagnosisForm.muskelmasse ? parseFloat(diagnosisForm.muskelmasse) : null,
        bmi: diagnosisForm.bmi ? parseFloat(diagnosisForm.bmi) : null,
        viszerales_fett: diagnosisForm.viszerales_fett ? parseFloat(diagnosisForm.viszerales_fett) : null,
        fettmasse: diagnosisForm.fettmasse ? parseFloat(diagnosisForm.fettmasse) : null,
        fettfreies_koerpergewicht: diagnosisForm.fettfreies_koerpergewicht ? parseFloat(diagnosisForm.fettfreies_koerpergewicht) : null,
        knochenmasse: diagnosisForm.knochenmasse ? parseFloat(diagnosisForm.knochenmasse) : null,
        wassergehalt: diagnosisForm.wassergehalt ? parseFloat(diagnosisForm.wassergehalt) : null,
        teilumfang: diagnosisForm.teilumfang ? parseFloat(diagnosisForm.teilumfang) : null,
        biologisches_alter: diagnosisForm.biologisches_alter ? parseInt(diagnosisForm.biologisches_alter) : null
      };
      
      if (editingDiagnosis) {
        await axios.put(`${API}/api/bio/diagnoses/${editingDiagnosis.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Diagnose aktualisiert");
      } else {
        await axios.post(`${API}/api/bio/diagnoses`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Diagnose erstellt");
      }
      setIsDiagnosisModalOpen(false);
      loadClientDetails(selectedClient.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDeleteDiagnosis = async (diagnosis) => {
    if (!window.confirm("Möchten Sie diese Diagnose löschen?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/bio/diagnoses/${diagnosis.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Diagnose gelöscht");
      loadClientDetails(selectedClient.id);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleSaveGoal = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        client_id: selectedClient.id,
        metric: goalForm.metric,
        target_value: parseFloat(goalForm.target_value),
        target_date: goalForm.target_date
      };
      await axios.post(`${API}/api/bio/goals`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ziel erstellt");
      setIsGoalModalOpen(false);
      loadGoals(selectedClient.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleToggleGoal = async (goal) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/bio/goals/${goal.id}`, { is_achieved: !goal.is_achieved }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(goal.is_achieved ? "Ziel wieder geöffnet" : "Ziel erreicht!");
      loadGoals(selectedClient.id);
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteGoal = async (goal) => {
    if (!window.confirm("Möchten Sie dieses Ziel löschen?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/bio/goals/${goal.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Ziel gelöscht");
      loadGoals(selectedClient.id);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const downloadPDF = async (lang = "de") => {
    if (!selectedClient) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/bio/clients/${selectedClient.id}/pdf?lang=${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = lang === "en" ? "Report" : "Bericht";
      link.setAttribute('download', `Tergoplus_${suffix}_${selectedClient.nachname}_${selectedClient.vorname}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(lang === "en" ? "PDF downloaded" : "PDF heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim PDF-Download");
    }
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const chartData = useMemo(() => {
    return diagnoses.map(d => ({
      datum: d.datum,
      ...Object.keys(MEASUREMENTS).reduce((acc, key) => { acc[key] = d[key]; return acc; }, {})
    }));
  }, [diagnoses]);

  const latestValues = useMemo(() => {
    if (diagnoses.length === 0) return {};
    return diagnoses[diagnoses.length - 1];
  }, [diagnoses]);

  const progress = useMemo(() => {
    if (diagnoses.length < 2) return {};
    const prev = diagnoses[diagnoses.length - 2];
    const last = diagnoses[diagnoses.length - 1];
    return Object.keys(MEASUREMENTS).reduce((acc, key) => {
      if (prev[key] != null && last[key] != null) {
        acc[key] = { change: (last[key] - prev[key]).toFixed(1), percent: (((last[key] - prev[key]) / prev[key]) * 100).toFixed(1) };
      }
      return acc;
    }, {});
  }, [diagnoses]);

  const renderChart = (measurementKey, showGoal = false) => {
    const config = MEASUREMENTS[measurementKey];
    const hasData = chartData.some(d => d[measurementKey] != null);
    const goal = goals.find(g => g.metric === measurementKey && !g.is_achieved);
    
    if (!hasData) {
      return <div className="h-48 flex items-center justify-center text-slate-400">Keine Daten verfügbar</div>;
    }

    return (
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`gradient-${measurementKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={config.color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={config.color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="datum" tick={{ fontSize: 11 }} tickFormatter={(val) => { const [y, m, d] = val.split('-'); return `${d}.${m}`; }} />
          <YAxis tick={{ fontSize: 11 }} width={40} />
          <Tooltip 
            contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
            formatter={(value) => [`${value} ${config.unit}`, config.label]}
            labelFormatter={(label) => { const [y, m, d] = label.split('-'); return `${d}.${m}.${y}`; }}
          />
          {showGoal && goal && (
            <ReferenceLine y={goal.target_value} stroke="#10b981" strokeDasharray="5 5" label={{ value: `Ziel: ${goal.target_value}`, position: 'right', fontSize: 10, fill: '#10b981' }} />
          )}
          <Area type="monotone" dataKey={measurementKey} stroke={config.color} strokeWidth={2} fill={`url(#gradient-${measurementKey})`} dot={{ fill: config.color, strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  // Compare calculations
  const compareData = useMemo(() => {
    if (!compareDiagnosis1 || !compareDiagnosis2) return null;
    const d1 = diagnoses.find(d => d.id === compareDiagnosis1);
    const d2 = diagnoses.find(d => d.id === compareDiagnosis2);
    if (!d1 || !d2) return null;
    
    return Object.keys(MEASUREMENTS).map(key => {
      const v1 = d1[key];
      const v2 = d2[key];
      const diff = (v1 != null && v2 != null) ? (v2 - v1).toFixed(1) : null;
      const isGood = diff !== null ? (MEASUREMENTS[key].lowerIsBetter ? parseFloat(diff) < 0 : parseFloat(diff) > 0) : null;
      return { key, label: MEASUREMENTS[key].label, unit: MEASUREMENTS[key].unit, v1, v2, diff, isGood };
    });
  }, [compareDiagnosis1, compareDiagnosis2, diagnoses]);

  return (
    <Layout onLogout={onLogout}>
      <div className="flex-1 overflow-auto p-6 bg-slate-50">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("bio_title")}</h1>
            <p className="text-slate-600">Kundenverwaltung und Diagnoseverlauf</p>
          </div>
          <Button onClick={() => openClientModal()} data-testid="add-client-button">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Kunde
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Client List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Kunden
                </CardTitle>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Suchen..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
                </div>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="text-center py-4 text-slate-500">Laden...</div>
                ) : filteredClients.length === 0 ? (
                  <div className="text-center py-4 text-slate-500">{searchQuery ? "Keine Ergebnisse" : "Keine Kunden"}</div>
                ) : (
                  <div className="space-y-2">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => setSelectedClient(client)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedClient?.id === client.id ? 'bg-slate-900 text-white' : 'bg-slate-100 hover:bg-slate-200'}`}
                        data-testid={`client-${client.id}`}
                      >
                        <div className="font-medium">{client.nachname}, {client.vorname}</div>
                        <div className={`text-sm ${selectedClient?.id === client.id ? 'text-slate-300' : 'text-slate-500'}`}>
                          {calculateAge(client.geburtsdatum)} Jahre • {client.groesse} cm
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {selectedClient ? (
              <div className="space-y-6">
                {/* Client Header */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start flex-wrap gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">{selectedClient.vorname} {selectedClient.nachname}</h2>
                        <div className="flex gap-4 mt-2 text-sm text-slate-600 flex-wrap">
                          <span>Geb.: {selectedClient.geburtsdatum}</span>
                          <span>Alter: {calculateAge(selectedClient.geburtsdatum)} Jahre</span>
                          <span>Größe: {selectedClient.groesse} cm</span>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => openClientModal(selectedClient)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteClient(selectedClient)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        <Button variant="outline" size="sm" onClick={() => setIsCompareModalOpen(true)} disabled={diagnoses.length < 2}><GitCompare className="w-4 h-4 mr-2" />Vergleichen</Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" data-testid="download-pdf-button"><FileDown className="w-4 h-4 mr-2" />PDF</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Sprache / Language</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => downloadPDF("de")} data-testid="pdf-deutsch">
                              <span className="mr-2">DE</span> Deutsch
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadPDF("en")} data-testid="pdf-english">
                              <span className="mr-2">EN</span> English
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button onClick={() => openDiagnosisModal()} data-testid="add-diagnosis-button"><Plus className="w-4 h-4 mr-2" />Neue Messung</Button>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    {diagnoses.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                        {["gewicht", "koerperfett", "muskelmasse", "bmi"].map((key) => {
                          const config = MEASUREMENTS[key];
                          const Icon = config.icon;
                          const value = latestValues[key];
                          const prog = progress[key];
                          const isGood = prog ? (config.lowerIsBetter ? parseFloat(prog.change) < 0 : parseFloat(prog.change) > 0) : null;
                          
                          return (
                            <div key={key} className="bg-slate-50 rounded-lg p-4">
                              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                                <Icon className="w-4 h-4" style={{ color: config.color }} />
                                {config.label}
                              </div>
                              <div className="text-2xl font-bold text-slate-900">
                                {value != null ? `${value}${config.unit ? ' ' + config.unit : ''}` : '-'}
                              </div>
                              {prog && (
                                <div className={`text-sm ${isGood ? 'text-green-500' : 'text-red-500'}`}>
                                  {parseFloat(prog.change) > 0 ? '+' : ''}{prog.change} {config.unit}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Goals Section */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        Persönliche Ziele
                      </CardTitle>
                      <Button size="sm" onClick={() => { setGoalForm({ metric: "gewicht", target_value: "", target_date: "" }); setIsGoalModalOpen(true); }}>
                        <Plus className="w-4 h-4 mr-1" />
                        Neues Ziel
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {goals.length === 0 ? (
                      <div className="text-center py-4 text-slate-500">Noch keine Ziele definiert</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {goals.map((goal) => {
                          const config = MEASUREMENTS[goal.metric];
                          const currentValue = latestValues[goal.metric];
                          const progressPercent = goal.start_value && currentValue ? Math.min(100, Math.max(0, ((goal.start_value - currentValue) / (goal.start_value - goal.target_value)) * 100)) : 0;
                          
                          return (
                            <div key={goal.id} className={`p-4 rounded-lg border ${goal.is_achieved ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  {goal.is_achieved ? (
                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                  ) : (
                                    <Circle className="w-5 h-5 text-slate-300" />
                                  )}
                                  <span className="font-medium">{config?.label || goal.metric}</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => handleToggleGoal(goal)}>
                                    {goal.is_achieved ? <Circle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-green-500" />}
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteGoal(goal)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Start:</span>
                                  <span>{goal.start_value || '-'} {config?.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Aktuell:</span>
                                  <span className="font-medium">{currentValue || '-'} {config?.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Ziel:</span>
                                  <span className="font-medium text-green-600">{goal.target_value} {config?.unit}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Bis:</span>
                                  <span>{goal.target_date}</span>
                                </div>
                              </div>
                              {!goal.is_achieved && goal.start_value && (
                                <div className="mt-3">
                                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-500 transition-all" style={{ width: `${progressPercent}%` }} />
                                  </div>
                                  <div className="text-xs text-center mt-1 text-slate-500">{progressPercent.toFixed(0)}% erreicht</div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Charts */}
                <Tabs defaultValue="uebersicht" className="w-full">
                  <TabsList className="grid grid-cols-4 lg:grid-cols-6 w-full">
                    <TabsTrigger value="uebersicht">Übersicht</TabsTrigger>
                    <TabsTrigger value="koerper">Körper</TabsTrigger>
                    <TabsTrigger value="fett">Fett</TabsTrigger>
                    <TabsTrigger value="muskel">Muskel</TabsTrigger>
                    <TabsTrigger value="andere">Andere</TabsTrigger>
                    <TabsTrigger value="tabelle">Tabelle</TabsTrigger>
                  </TabsList>

                  <TabsContent value="uebersicht" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {["gewicht", "koerperfett", "muskelmasse", "bmi"].map((key) => (
                        <Card key={key}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MEASUREMENTS[key].color }} />
                              {MEASUREMENTS[key].label}
                              {goals.find(g => g.metric === key && !g.is_achieved) && <Badge variant="outline" className="ml-2 text-green-600">Ziel</Badge>}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>{renderChart(key, true)}</CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="koerper" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {["gewicht", "bmi", "teilumfang", "biologisches_alter"].map((key) => (
                        <Card key={key}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MEASUREMENTS[key].color }} />
                              {MEASUREMENTS[key].label}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>{renderChart(key, true)}</CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="fett" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {["koerperfett", "viszerales_fett", "fettmasse", "fettfreies_koerpergewicht"].map((key) => (
                        <Card key={key}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MEASUREMENTS[key].color }} />
                              {MEASUREMENTS[key].label}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>{renderChart(key, true)}</CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="muskel" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {["muskelmasse", "knochenmasse", "wassergehalt"].map((key) => (
                        <Card key={key}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MEASUREMENTS[key].color }} />
                              {MEASUREMENTS[key].label}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>{renderChart(key, true)}</CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="andere" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {["teilumfang", "viszerales_fett", "biologisches_alter", "wassergehalt"].map((key) => (
                        <Card key={key}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: MEASUREMENTS[key].color }} />
                              {MEASUREMENTS[key].label}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>{renderChart(key, true)}</CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="tabelle" className="mt-4">
                    <Card>
                      <CardContent className="pt-6 overflow-x-auto">
                        {diagnoses.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">Noch keine Messungen vorhanden</div>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-3 px-2 font-semibold">Datum</th>
                                <th className="text-left py-3 px-2 font-semibold">Typ</th>
                                <th className="text-left py-3 px-2 font-semibold whitespace-nowrap">Nächste</th>
                                {Object.entries(MEASUREMENTS).map(([key, config]) => (
                                  <th key={key} className="text-right py-3 px-2 font-semibold whitespace-nowrap">{config.label}</th>
                                ))}
                                <th className="py-3 px-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...diagnoses].reverse().map((d) => (
                                <tr key={d.id} className="border-b hover:bg-slate-50">
                                  <td className="py-3 px-2 font-medium">{d.datum}</td>
                                  <td className="py-3 px-2">
                                    {d.typ ? (
                                      <Badge variant="outline" className="whitespace-nowrap">{d.typ}</Badge>
                                    ) : '-'}
                                  </td>
                                  <td className="py-3 px-2 whitespace-nowrap">
                                    {d.naechste_bewertung || '-'}
                                  </td>
                                  {Object.entries(MEASUREMENTS).map(([key, config]) => (
                                    <td key={key} className="text-right py-3 px-2">{d[key] != null ? `${d[key]}${config.unit ? ' ' + config.unit : ''}` : '-'}</td>
                                  ))}
                                  <td className="py-3 px-2">
                                    <div className="flex gap-1 justify-end">
                                      <Button variant="ghost" size="sm" onClick={() => openDiagnosisModal(d)}><Pencil className="w-4 h-4" /></Button>
                                      <Button variant="ghost" size="sm" onClick={() => handleDeleteDiagnosis(d)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <Card className="h-[400px] flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Wählen Sie einen Kunden aus der Liste</p>
                  <p className="text-sm mt-1">oder erstellen Sie einen neuen Kunden</p>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Client Modal */}
        <Dialog open={isClientModalOpen} onOpenChange={setIsClientModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingClient ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveClient} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vorname *</Label>
                  <Input value={clientForm.vorname} onChange={(e) => setClientForm({...clientForm, vorname: e.target.value})} required data-testid="client-vorname-input" />
                </div>
                <div className="space-y-2">
                  <Label>Nachname *</Label>
                  <Input value={clientForm.nachname} onChange={(e) => setClientForm({...clientForm, nachname: e.target.value})} required data-testid="client-nachname-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Geburtsdatum *</Label>
                  <DatePicker 
                    value={clientForm.geburtsdatum} 
                    onChange={(date) => setClientForm({...clientForm, geburtsdatum: date})}
                    placeholder="Geburtsdatum wählen"
                    data-testid="client-geburtsdatum-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Größe (cm) *</Label>
                  <Input type="number" step="0.1" value={clientForm.groesse} onChange={(e) => setClientForm({...clientForm, groesse: e.target.value})} required data-testid="client-groesse-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsClientModalOpen(false)}>Abbrechen</Button>
                <Button type="submit" data-testid="save-client-button">{editingClient ? "Speichern" : "Erstellen"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Diagnosis Modal */}
        <Dialog open={isDiagnosisModalOpen} onOpenChange={setIsDiagnosisModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingDiagnosis ? "Messung bearbeiten" : "Neue Messung"}</DialogTitle>
              <DialogDescription>Geben Sie die Messwerte der Bioimpedanz-Analyse ein</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveDiagnosis} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Datum *</Label>
                  <DatePicker 
                    value={diagnosisForm.datum} 
                    onChange={(date) => setDiagnosisForm({...diagnosisForm, datum: date})}
                    placeholder="Messdatum wählen"
                    data-testid="diagnosis-datum-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Typ der Messung</Label>
                  <Select value={diagnosisForm.typ} onValueChange={(v) => setDiagnosisForm({...diagnosisForm, typ: v})}>
                    <SelectTrigger data-testid="diagnosis-typ-select">
                      <SelectValue placeholder="Typ wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Erstanalyse">Erstanalyse</SelectItem>
                      <SelectItem value="Monatlicher Check-in">Monatlicher Check-in</SelectItem>
                      <SelectItem value="Wöchentlicher Check-in">Wöchentlicher Check-in</SelectItem>
                      <SelectItem value="Zwischenmessung">Zwischenmessung</SelectItem>
                      <SelectItem value="Abschlussmessung">Abschlussmessung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nächste Bewertung</Label>
                  <DatePicker 
                    value={diagnosisForm.naechste_bewertung} 
                    onChange={(date) => setDiagnosisForm({...diagnosisForm, naechste_bewertung: date})}
                    placeholder="Nächstes Datum"
                    data-testid="diagnosis-naechste-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(MEASUREMENTS).map(([key, config]) => (
                  <div key={key} className="space-y-2">
                    <Label>{config.label} ({config.unit || '-'})</Label>
                    <Input type="number" step="0.1" value={diagnosisForm[key]} onChange={(e) => setDiagnosisForm({...diagnosisForm, [key]: e.target.value})} data-testid={`diagnosis-${key}-input`} />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDiagnosisModalOpen(false)}>Abbrechen</Button>
                <Button type="submit" data-testid="save-diagnosis-button">{editingDiagnosis ? "Speichern" : "Erstellen"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Goal Modal */}
        <Dialog open={isGoalModalOpen} onOpenChange={setIsGoalModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Neues Ziel erstellen</DialogTitle>
              <DialogDescription>Definieren Sie ein persönliches Ziel für den Kunden</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveGoal} className="space-y-4">
              <div className="space-y-2">
                <Label>Messwert</Label>
                <Select value={goalForm.metric} onValueChange={(v) => setGoalForm({...goalForm, metric: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MEASUREMENTS).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zielwert ({MEASUREMENTS[goalForm.metric]?.unit || '-'})</Label>
                <Input type="number" step="0.1" value={goalForm.target_value} onChange={(e) => setGoalForm({...goalForm, target_value: e.target.value})} required placeholder={`z.B. ${goalForm.metric === 'gewicht' ? '75' : goalForm.metric === 'koerperfett' ? '18' : '40'}`} />
              </div>
              <div className="space-y-2">
                <Label>Zieldatum</Label>
                <DatePicker 
                  value={goalForm.target_date} 
                  onChange={(date) => setGoalForm({...goalForm, target_date: date})}
                  placeholder="Zieldatum wählen"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsGoalModalOpen(false)}>Abbrechen</Button>
                <Button type="submit">Ziel erstellen</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Compare Modal */}
        <Dialog open={isCompareModalOpen} onOpenChange={setIsCompareModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GitCompare className="w-5 h-5" />
                Messungen vergleichen
              </DialogTitle>
              <DialogDescription>Wählen Sie zwei Messungen für den Vergleich</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <Label>Erste Messung</Label>
                <Select value={compareDiagnosis1 || ""} onValueChange={setCompareDiagnosis1}>
                  <SelectTrigger><SelectValue placeholder="Messung wählen..." /></SelectTrigger>
                  <SelectContent>
                    {diagnoses.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.datum}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Zweite Messung</Label>
                <Select value={compareDiagnosis2 || ""} onValueChange={setCompareDiagnosis2}>
                  <SelectTrigger><SelectValue placeholder="Messung wählen..." /></SelectTrigger>
                  <SelectContent>
                    {diagnoses.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.datum}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {compareData && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left py-3 px-4 font-semibold">Messwert</th>
                      <th className="text-center py-3 px-4 font-semibold">{diagnoses.find(d => d.id === compareDiagnosis1)?.datum}</th>
                      <th className="text-center py-3 px-4 font-semibold">{diagnoses.find(d => d.id === compareDiagnosis2)?.datum}</th>
                      <th className="text-center py-3 px-4 font-semibold">Differenz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareData.map((row) => (
                      <tr key={row.key} className="border-t hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium">{row.label}</td>
                        <td className="py-3 px-4 text-center">{row.v1 != null ? `${row.v1} ${row.unit}` : '-'}</td>
                        <td className="py-3 px-4 text-center">{row.v2 != null ? `${row.v2} ${row.unit}` : '-'}</td>
                        <td className={`py-3 px-4 text-center font-semibold ${row.isGood === true ? 'text-green-600' : row.isGood === false ? 'text-red-600' : 'text-slate-500'}`}>
                          {row.diff != null ? (
                            <>
                              {parseFloat(row.diff) > 0 ? '+' : ''}{row.diff} {row.unit}
                              {row.isGood === true && ' ✓'}
                            </>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCompareModalOpen(false)}>Schließen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
